/**
 * Pyrite company logos: the buyer page (W520, A1, PYR-251).
 *
 * W520 A2b: talks only to the copy's web address, which the section reads from the store (a
 * Google Apps Script address), directly, as the live helper does: JSON sent as text/plain so there is no
 * CORS preflight, Google's redirect to its answer page followed, and no cookies sent. Shopify's
 * app proxy is not used; it cannot reach Apps Script (S6 item 1). Who is signed in comes from
 * the page pass that Liquid signs on Shopify's server.
 *
 * W520 A7d (W518 S6 Part 4): the page's own HTML holds no pass, because the browser may keep that HTML
 * and show it again (Back after a sign-out, a restored tab). It says only whether Shopify saw a
 * signed-in customer. The pass comes from the separate section pyrite-company-logos-pass, fetched with
 * no-store each time the page starts, and it is kept only in memory. The page renews it before it is
 * 8 minutes old, and once more if the copy says it expired or did not check out (a changed key,
 * W519 K6). A page the browser keeps in its memory is cleared as it is put away and starts again when
 * it comes back. Sign-in is Shopify's own (one-time code by email). The page never sees
 * an email address, a Drive link or a file ID, and it logs nothing. Every refusal shows the
 * server's plain message; nothing fails open.
 */
(function () {
  'use strict';
  var Core = window.PyriteCompanyLogosCore;
  var root = document.querySelector('[data-pyrite-company-logos]');
  if (!Core || !root || !window.fetch || !window.crypto || !window.crypto.getRandomValues || !window.FileReader || !window.DOMParser) return;

  var MAX_BYTES = 10 * 1024 * 1024;
  var itemsNode = root.querySelector('[data-items]');
  var items = Core.parseItems(itemsNode ? itemsNode.textContent : '[]');
  var hostsNode = root.querySelector('[data-checkout-hosts]');
  var hosts = Core.parseHosts(hostsNode ? hostsNode.textContent : '[]');
  var endpointNode = root.querySelector('[data-endpoint]');
  var endpoint = Core.parseEndpoint(endpointNode ? endpointNode.textContent : '""');
  var signedInNode = root.querySelector('[data-signed-in]');
  var renderedSignedIn = !!signedInNode && signedInNode.textContent.trim() === 'true';
  var PASS_SECTION = 'pyrite-company-logos-pass';
  var PASS_RENEW_MS = 8 * 60 * 1000;
  var pass = {value: '', at: 0};
  // Each start of the page is a new visit. An answer to a request from an earlier visit is dropped.
  var visit = 0;
  // W531: the product page sends buyers here as ?product=<handle>. Only these exact handles count;
  // anything else is ignored, and the value is never written into the page.
  var PRODUCT_PAGES = {'pemb-training-model': {family: 'training', back: 'Back to the Training Model'},
    'micro-buildings': {family: 'micros', back: 'Back to Micro Buildings'}};
  var fromProduct = (function () {
    var match = /[?&]product=([a-z0-9-]{1,64})(?:&|$)/.exec(String(window.location.search || ''));
    return match && Object.prototype.hasOwnProperty.call(PRODUCT_PAGES, match[1]) ? match[1] : null;
  })();
  var backBox = null;
  var el = {
    status: root.querySelector('[data-status]'),
    signin: root.querySelector('[data-signin]'),
    form: root.querySelector('[data-form]'),
    logos: root.querySelector('[data-logos]'),
    upload: root.querySelector('[data-upload]'),
    file: root.querySelector('[data-file]'),
    fileNote: root.querySelector('[data-file-note]'),
    list: root.querySelector('[data-items-list]'),
    setup: root.querySelector('[data-setup]'),
    subtotal: root.querySelector('[data-subtotal]'),
    message: root.querySelector('[data-message]'),
    submit: root.querySelector('[data-submit]')
  };

  function fresh() {
    return {library: null, op: null, choice: null, file: null, reading: false, busy: false, requestId: null, quantities: {}, previews: {}};
  }

  var state = fresh();

  function random(n) {
    var bytes = new Uint8Array(n);
    window.crypto.getRandomValues(bytes);
    return bytes;
  }

  /**
   * A fresh page pass from Shopify: the pass section rendered for the current session, fetched with
   * no-store so the browser never keeps it. A signed-out session (or any failure) gives '' and the
   * page simply continues signed out. A pass that arrives after the page was put away is dropped.
   */
  function renewPass() {
    var mine = visit;
    var address = window.location.pathname + '?section_id=' + PASS_SECTION;
    return window.fetch(address, {credentials: 'same-origin', cache: 'no-store'})
      .then(function (response) { return response.ok ? response.text() : ''; })
      .then(function (html) {
        var found = html ? new window.DOMParser().parseFromString(html, 'text/html').querySelector('[data-pass]') : null;
        return Core.parsePass(found ? found.textContent : '""');
      })
      .catch(function () { return ''; })
      .then(function (value) {
        if (mine === visit) pass = {value: value, at: Date.now()};
      });
  }

  function send(action, body) {
    // W519 (can wait from A7c, done here since this script changes anyway): the body must be for this action.
    if (!body || body.action !== action) return Promise.resolve(Core.parseAnswer(''));
    return window.fetch(Core.url(action, endpoint), {
      method: 'POST',
      headers: {'Content-Type': 'text/plain;charset=utf-8'},
      body: JSON.stringify(Core.withPass(body, pass.value)),
      credentials: 'omit',
      redirect: 'follow',
      cache: 'no-store'
    }).then(function (response) { return response.text(); })
      .then(Core.parseAnswer, function () { return Core.parseAnswer(''); });
  }

  function post(action, body) {
    if (!endpoint) return Promise.resolve(Core.parseAnswer(''));
    // A request belongs to the visit that made it (W519, A7d v1 review): once the page has been put
    // away, it is neither sent nor sent again, so it never goes out with a later visit's pass.
    var mine = visit;
    var ready = pass.value && Date.now() - pass.at > PASS_RENEW_MS ? renewPass() : Promise.resolve();
    return ready.then(function () {
      return mine === visit ? send(action, body) : Core.parseAnswer('');
    }).then(function (answer) {
      if (mine !== visit || !answer || (answer.code !== 'SESSION_EXPIRED' && answer.code !== 'SIGN_IN_INVALID') || !pass.value) return answer;
      // The pass may have aged past 10 minutes (a sleeping laptop), or the key may just have been
      // changed (W519 K6): renew it from Shopify and try once more.
      var again = {};
      Object.keys(body).forEach(function (name) { again[name] = body[name]; });
      again.n = Core.nonce(random);
      return renewPass().then(function () {
        return mine === visit ? send(action, again) : Core.parseAnswer('');
      });
    });
  }

  function make(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function say(node, text) {
    if (node) node.textContent = text || '';
  }

  /**
   * Any change of choices starts a new checkout request; a retry of the same choices keeps it.
   * The item list is rebuilt only when the logo choice changes, so a quantity pick keeps focus.
   */
  function changed(rebuildItems) {
    state.requestId = null;
    say(el.message, '');
    render(rebuildItems);
  }

  function approvedFor(logo) {
    var parts = [];
    if (logo.products.micros) parts.push('Micros');
    if (logo.products.training) parts.push('the Training Model');
    return parts.length ? 'Approved for ' + parts.join(' and ') + '.' : 'Not approved for these items yet. Please email us.';
  }

  function showPreview(logo, holder, button) {
    if (state.previews[logo.key]) {
      holder.hidden = false;
      return;
    }
    var mine = visit;
    button.disabled = true;
    say(button, 'Loading preview…');
    post('preview', Core.previewBody(random, logo.key)).then(function (answer) {
      if (mine !== visit) return;
      var source = Core.previewSource(answer);
      button.disabled = false;
      if (!source) {
        say(button, 'Preview not available');
        return;
      }
      state.previews[logo.key] = source;
      var image = make('img', 'pcl__preview');
      image.alt = 'Preview of ' + logo.label;
      image.src = source;
      holder.textContent = '';
      holder.appendChild(image);
      holder.hidden = false;
      say(button, 'Preview shown');
    });
  }

  function renderLogos() {
    var library = state.library;
    el.logos.textContent = '';
    library.logos.forEach(function (logo, index) {
      var id = 'pcl-logo-' + index;
      var card = make('div', 'pcl__choice');
      var input = make('input');
      input.type = 'radio';
      input.name = 'pcl-logo';
      input.id = id;
      input.checked = !!(state.choice && state.choice.kind === 'saved' && state.choice.key === logo.key);
      input.addEventListener('change', function () {
        state.choice = {kind: 'saved', key: logo.key};
        changed(true);
      });
      var label = make('label', 'pcl__choice-label');
      label.htmlFor = id;
      label.appendChild(make('span', 'pcl__choice-title', logo.label));
      label.appendChild(make('span', 'pcl__choice-note', approvedFor(logo) + ' Logo setup is $0.00.'));
      card.appendChild(input);
      card.appendChild(label);
      if (logo.preview) {
        var holder = make('div', 'pcl__preview-holder');
        holder.hidden = true;
        if (state.previews[logo.key]) {
          var image = make('img', 'pcl__preview');
          image.alt = 'Preview of ' + logo.label;
          image.src = state.previews[logo.key];
          holder.appendChild(image);
          holder.hidden = false;
        }
        var button = make('button', 'pcl__link', state.previews[logo.key] ? 'Preview shown' : 'Show preview');
        button.type = 'button';
        button.addEventListener('click', function () { showPreview(logo, holder, button); });
        card.appendChild(button);
        card.appendChild(holder);
      }
      el.logos.appendChild(card);
    });
    var uploadCard = make('div', 'pcl__choice');
    var uploadInput = make('input');
    uploadInput.type = 'radio';
    uploadInput.name = 'pcl-logo';
    uploadInput.id = 'pcl-logo-upload';
    uploadInput.checked = !!(state.choice && state.choice.kind === 'upload');
    uploadInput.addEventListener('change', function () {
      state.choice = {kind: 'upload'};
      changed(true);
    });
    var uploadLabel = make('label', 'pcl__choice-label');
    uploadLabel.htmlFor = 'pcl-logo-upload';
    uploadLabel.appendChild(make('span', 'pcl__choice-title', library.logos.length ? 'Use a new or different logo' : 'Upload your logo'));
    uploadLabel.appendChild(make('span', 'pcl__choice-note', 'We check every new logo before printing. ' +
      (library.fee ? 'One-time setup: ' + Core.money(library.fee) + '.' : 'A one-time setup fee applies.')));
    uploadCard.appendChild(uploadInput);
    uploadCard.appendChild(uploadLabel);
    el.logos.appendChild(uploadCard);
  }

  // W531b (Justice, DEC-678): click to choose, with no number boxes. The Training Model is one option and
  // Micro Buildings are one batch size; click a chosen one again to remove it. Each chosen line is
  // quantity 1, so the request to the copy is unchanged. W531c (DEC-679): they are radio buttons, one
  // group per product, so they look exactly like the "Choose your logo" cards with no CSS change.
  var CUSTOM_ORDER_EMAIL = 'justice@pyriteprinting.com';
  var itemInputs = {};

  function pick(item, on) {
    items.forEach(function (other) {
      if (other.family !== item.family) return;
      state.quantities[other.variantId] = 0;
      if (itemInputs[other.variantId]) itemInputs[other.variantId].checked = false;
    });
    if (on) {
      state.quantities[item.variantId] = 1;
      itemInputs[item.variantId].checked = true;
    }
    changed(false);
  }

  function renderItems() {
    el.list.textContent = '';
    itemInputs = {};
    items.forEach(function (item) {
      var allowed = Core.familyAllowed(state.choice, state.library, item.family);
      if (!allowed && state.quantities[item.variantId]) state.quantities[item.variantId] = 0;
      var id = 'pcl-item-' + item.variantId;
      var card = make('div', 'pcl__choice' + (allowed ? '' : ' pcl__item--off'));
      var input = make('input', 'pcl__pick');
      input.type = 'radio';
      input.name = 'pcl-item-' + item.family;
      input.id = id;
      input.checked = !!state.quantities[item.variantId];
      input.disabled = !allowed;
      // A click on the chosen option removes it (a radio fires no change then); choosing one fires change.
      input.addEventListener('click', function () {
        if (state.quantities[item.variantId]) pick(item, false);
      });
      input.addEventListener('change', function () {
        if (input.checked && !state.quantities[item.variantId]) pick(item, true);
      });
      itemInputs[item.variantId] = input;
      var label = make('label', 'pcl__choice-label');
      label.htmlFor = id;
      label.appendChild(make('span', 'pcl__choice-title', item.label));
      label.appendChild(make('span', 'pcl__choice-note', !allowed && state.choice ? 'Not available with the logo you chose.' : (item.price || '')));
      card.appendChild(input);
      card.appendChild(label);
      el.list.appendChild(card);
    });
    var custom = el.list.appendChild(make('p', 'pcl__note', 'Need a different quantity or a custom order? '));
    var mail = custom.appendChild(make('a', 'pcl__link', 'Email Justice'));
    mail.href = 'mailto:' + CUSTOM_ORDER_EMAIL;
    custom.appendChild(make('span', '', '.'));
  }

  function render(rebuildItems) {
    var library = state.library;
    el.upload.hidden = !(state.choice && state.choice.kind === 'upload');
    say(el.setup, Core.setupLine(state.choice, library));
    var cents = Core.subtotalCents(items, state.quantities, state.choice, library);
    say(el.subtotal, cents === null ? '' : 'Subtotal before shipping and tax: ' + Core.moneyCents(cents) + '.');
    if (rebuildItems) renderItems();
    el.submit.disabled = state.busy || state.reading;
    say(el.submit, state.busy ? 'Preparing your secure checkout…' : 'Continue to secure checkout');
  }

  function showLibrary(answer) {
    var library = Core.readLibrary(answer);
    if (!library) {
      say(el.status, answer && answer.message ? answer.message : Core.GENERIC);
      return false;
    }
    state.library = library;
    state.op = library.op;
    if (library.signedIn && library.logos.length) {
      say(el.status, 'You\'re signed in. ' + (library.company ? 'Your company\'s logos (' + library.company + '):' : 'Your logos:'));
      el.signin.hidden = true;
    } else if (library.signedIn) {
      // W531: nothing on file goes back to the product page, where a past buyer's free setup still works.
      say(el.status, 'You\'re signed in, but there\'s no saved logo on your account yet. Go back to the product page to add your branding there. If you ordered a logo from us before, the setup you paid for still counts there.');
      el.signin.hidden = true;
      el.form.hidden = true;
      showBack(true);
      return true;
    } else {
      say(el.status, 'Sign in with your work email to use your company\'s saved logo. Shopify emails you a one-time code. Or upload a new logo below.');
      el.signin.hidden = false;
    }
    if (!state.choice || (state.choice.kind === 'saved' && !library.logos.some(function (logo) { return logo.key === state.choice.key; }))) {
      state.choice = library.logos.length ? {kind: 'saved', key: library.logos[0].key} : {kind: 'upload'};
    }
    showBack(false);
    if (fromProduct && library.signedIn) preselect(PRODUCT_PAGES[fromProduct].family);
    el.form.hidden = false;
    renderLogos();
    render(true);
    return true;
  }

  /** W531: links back to the product page (only the known handles), with "Add your branding" open. */
  function showBack(on) {
    if (!on) {
      if (backBox) backBox.hidden = true;
      return;
    }
    if (!backBox) backBox = root.appendChild(make('p', 'pcl__back'));
    backBox.textContent = '';
    backBox.hidden = false;
    (fromProduct ? [fromProduct] : Object.keys(PRODUCT_PAGES)).forEach(function (handle) {
      var link = backBox.appendChild(make('a', 'button button--secondary', PRODUCT_PAGES[handle].back));
      link.href = '/products/' + handle + '?pyrite_branding=1';
    });
  }

  /** W531: a product with a single item on this page starts at 1, if nothing is set and the chosen logo may go on it. */
  function preselect(family) {
    var matches = items.filter(function (item) { return item.family === family; });
    var anySet = Object.keys(state.quantities).some(function (id) { return !!state.quantities[id]; });
    if (matches.length !== 1 || anySet || !Core.familyAllowed(state.choice, state.library, family)) return;
    state.quantities[matches[0].variantId] = 1;
  }

  function loadLibrary() {
    var mine = visit;
    return post('library', Core.libraryBody(random)).then(function (answer) {
      if (mine === visit) showLibrary(answer);
    });
  }

  /**
   * Back to how the page first loads: no pass, no library, nothing chosen, and nothing on screen that
   * belongs to a customer. Used as the browser puts the page away in its memory and when it brings it back.
   */
  function forget() {
    visit += 1;
    pass = {value: '', at: 0};
    state = fresh();
    el.form.hidden = true;
    el.signin.hidden = true;
    el.upload.hidden = true;
    el.logos.textContent = '';
    el.list.textContent = '';
    el.file.value = '';
    say(el.fileNote, '');
    say(el.setup, '');
    say(el.subtotal, '');
    say(el.message, '');
    say(el.status, 'Loading your saved logos…');
  }

  /**
   * Asks Shopify for a fresh pass when the page was rendered for a signed-in customer, or always when
   * the page comes back from the browser's memory, then loads the library.
   */
  function start(always) {
    var mine = visit;
    var ready = always || renderedSignedIn ? renewPass() : Promise.resolve();
    return ready.then(function () {
      if (mine === visit) return loadLibrary();
    });
  }

  el.file.addEventListener('change', function () {
    var picked = el.file.files && el.file.files[0];
    state.file = null;
    say(el.fileNote, '');
    changed();
    if (!picked) return;
    if (picked.size > MAX_BYTES) {
      say(el.fileNote, 'Your file is larger than 10 MB. Please upload a smaller version.');
      return;
    }
    var mine = visit;
    state.reading = true;
    render();
    var reader = new window.FileReader();
    reader.onload = function () {
      if (mine !== visit) return;
      state.reading = false;
      var made = Core.uploadFile(picked.name, new Uint8Array(reader.result), MAX_BYTES);
      if (made.problem) say(el.fileNote, made.problem);
      else {
        state.file = made.file;
        say(el.fileNote, 'Ready: ' + picked.name);
      }
      render();
    };
    reader.onerror = function () {
      if (mine !== visit) return;
      state.reading = false;
      say(el.fileNote, 'That file could not be read. Please choose it again.');
      render();
    };
    reader.readAsArrayBuffer(picked);
  });

  el.form.addEventListener('submit', function (event) {
    event.preventDefault();
    if (state.busy || state.reading) return;
    if (!state.requestId) state.requestId = Core.requestId(random);
    var made = Core.prepareBody(random, {requestId: state.requestId, op: state.op, choice: state.choice, file: state.file}, items, state.quantities, state.library);
    if (made.problem) {
      say(el.message, made.problem);
      return;
    }
    var mine = visit;
    state.busy = true;
    say(el.message, '');
    render();
    post('prepare', made.body).then(function (answer) {
      if (mine !== visit) return;
      var next = Core.checkoutUrl(answer, hosts);
      if (next) {
        window.location.assign(next);
        return;
      }
      state.busy = false;
      say(el.message, answer && answer.message ? answer.message : Core.GENERIC);
      if (!answer || !answer.retry) state.requestId = null;
      if (answer && (answer.code === 'SESSION_EXPIRED' || answer.code === 'REQUEST_EXPIRED' || answer.code === 'CHECKOUT_REPLACED')) {
        state.requestId = null;
        loadLibrary();
      }
      render();
    });
  });

  // A7d: the browser's back-forward cache keeps the page in memory, with its pass and the library on
  // screen. Both are cleared whenever the page is put away (W519 R2), and the page starts again when
  // it comes back, so Back after a sign-out shows nobody's logos.
  window.addEventListener('pagehide', function () {
    forget();
  });
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    forget();
    start(true);
  });

  start(false);
})();
